import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { PresaleItems } from "./Components/PresaleItems";
import { PresaleForm } from "./Components/PresaleForm";

import {
  ThemeProvider,
  Drawer,
  IconButton,
  Fab,
  Alert,
  AlertTitle,
} from "@mui/material";
import { createCustomTheme } from "Components/Styles/Styles";
import CssBaseline from "@mui/material/CssBaseline";

// assets
import { Typography, Stack, Button, Box, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  CardStyle,
  ContentWrapNarrow,
  CreatedWith,
  InfoBox,
  Loading,
} from "Components/AllComponents";
import Avatar from "boring-avatars";
import { fetchPresale, readSellerInfo } from "Util/firebaseHelper";
import {
  getPresaleName,
  PresaleInfo,
  formatFulfillments,
} from "Model/PresaleInfo";
import { isInAppBrowser } from "Util/helpers";
import { AvailabilityType } from "Model/Availability";
import { Profile, defaultProfile, defaultSquareState } from "Model/Profile";
import { getNewDateWithoutHours } from "Util/dateUtil";
import { Timestamp } from "firebase/firestore";
import {
  DEFAULT_COLOR_PALETTE,
  PERSONALIZATION_TYPE_CUSTOM_ONLY,
  PRESALE_STATUS_ENDED,
  PRESALE_STATUS_NOTPUBLISHED,
  PAYMENT_TYPE_CREDIT_CARD,
} from "Constants";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { initializeCart } from "Redux/actionCreators";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { SubAppBar } from "Components/CustomerView/SubAppBar";
import { como } from "Components/Styles/colors";
import { PresaleItem } from "Model/PresaleItem";

const drawerWidth = 390;

const CustomerPresale = () => {
  const { presaleId } = useParams<"presaleId">();
  let { userId } = useParams<"userId">();
  userId = userId?.toLowerCase();
  const [sellerProfile, setSellerProfile] = useState<Profile>(defaultProfile);
  const [presaleInfo, setPresaleInfo] = useState<PresaleInfo>();
  const [loading, setLoading] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [showInAppBrowserWarning, setShowInAppBrowserWarning] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Detect Instagram/Facebook in-app browser
  useEffect(() => {
    setShowInAppBrowserWarning(isInAppBrowser());
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      if (userId) {
        const form = await readSellerInfo(userId);
        let data = form.data();
        if (data) {
          var type =
            (data?.availability?.type === AvailabilityType.acceptBetween ||
              data?.availability?.type === AvailabilityType.acceptUntil) &&
            (data?.availability.end instanceof Timestamp
              ? data?.availability.end.toDate().toISOString()
              : data?.availability?.end) < getNewDateWithoutHours()
              ? AvailabilityType.doNotAcceptOrder
              : data?.availability?.type || AvailabilityType.acceptNow;
          document.title = data?.businessName;
          setSellerProfile({
            businessName: data?.businessName,
            outsideUS: data?.outsideUS || false,
            sellerName: data?.sellerName || "",
            sellerIntro: data?.sellerIntro || "",
            sellerCity: data?.sellerCity || "",
            sellerState: data?.sellerState || "",
            sellerZip: data?.sellerZip || "",
            shipOnlyToMyState: data?.shipOnlyToMyState || false,
            fulfillmentMethod: data?.fulfillmentMethod || "Pickup only",
            defaultPickupAddress: data?.defaultPickupAddress || "",
            sellerPhoto: data?.sellerPhoto || "",
            sellerPortrait: data?.sellerPortrait || "",
            username: data?.username || "",
            links: data?.links || [],
            forms: data?.forms || [],
            availability: {
              type: type,
              start:
                (data?.availability.start instanceof Timestamp
                  ? data?.availability.start.toDate().toISOString()
                  : data?.availability?.start) || getNewDateWithoutHours(),
              end:
                (data?.availability.end instanceof Timestamp
                  ? data?.availability.end.toDate().toISOString()
                  : data?.availability?.end) || getNewDateWithoutHours(),
              lead: data?.availability?.lead || 1,
              dateBlocked: data?.availability?.dateBlocked || [],
            },
            presales: data?.presales || [],
            livePresales: data?.livePresales || [],
            payments: data?.payments || [],
            uid: data?.uid || "", // need for checkout
            sellerEmail: data?.sellerEmail || "",
            colorPalette: data?.colorPalette || DEFAULT_COLOR_PALETTE,
            squareState: data?.squareState || defaultSquareState,
            disableCustomerEmailList: data?.disableCustomerEmailList || false,
            imageGallery: data?.imageGallery || [],
            hideImageGallery: data?.hideImageGallery || false,
          });
        }
      }
      if (presaleId) {
        const presale = await fetchPresale(presaleId);
        if (presale === null) {
        } else {
          console.log("presale data", presale.data());
          let content = presale.data();
          if (content) {
            const presaleInfo = content as PresaleInfo;
            const updatedPresaleItems: PresaleItem[] =
              presaleInfo.presaleItems.map((item) => ({
                ...item,
                flavors: item.flavors || [],
                colors: item.colors || [],
                personalizationType:
                  item.personalizationType || PERSONALIZATION_TYPE_CUSTOM_ONLY,
                personalizationOptions: item.personalizationOptions || [],
              }));
            setPresaleInfo({
              ...presaleInfo,
              presaleItems: updatedPresaleItems,
            });
            dispatch(initializeCart(updatedPresaleItems));
          }
        }
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <ThemeProvider theme={createCustomTheme(sellerProfile.colorPalette)}>
      <CssBaseline />
      {loading ? (
        <Loading />
      ) : !presaleInfo || presaleInfo.status === PRESALE_STATUS_NOTPUBLISHED ? (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{
            backgroundColor: "background.default",
            minHeight: "100vh",
            p: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Presale doesn't exist
          </Typography>
          <Button
            variant="outlined"
            startIcon={<StorefrontOutlinedIcon />}
            sx={{ width: "fit-content" }}
            onClick={() => {
              navigate(`/${sellerProfile.username}`);
            }}
          >
            {sellerProfile.businessName}
          </Button>
        </Stack>
      ) : presaleInfo.status === PRESALE_STATUS_ENDED ? (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{
            backgroundColor: "background.default",
            minHeight: "100vh",
            p: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Presale has ended
          </Typography>
          <Button
            variant="outlined"
            startIcon={<StorefrontOutlinedIcon />}
            sx={{ width: "fit-content" }}
            onClick={() => {
              navigate(`/${sellerProfile.username}`);
            }}
          >
            {sellerProfile.businessName}
          </Button>
        </Stack>
      ) : (
        <>
          {/* Main Content */}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              mr: { md: `${drawerWidth}px` },
            }}
          >
            <Stack
              spacing={{ xs: 2, sm: 3 }}
              sx={{
                p: { xs: 2, sm: 3 },
                pb: { xs: 10, md: 3 },
                backgroundColor: "background.default",
                minHeight: "100vh",
              }}
            >
              <SubAppBar
                sellerName={sellerProfile.sellerName}
                sellerPhoto={sellerProfile.sellerPhoto}
                username={sellerProfile.username}
                businessName={sellerProfile.businessName}
              />

              {/* Mobile Cart Button */}
              <Box
                sx={{
                  display: { xs: "block", md: "none" },
                  position: "fixed",
                  bottom: 16,
                  right: 16,
                  zIndex: 1100,
                }}
              >
                <Fab
                  variant="extended"
                  color="primary"
                  onClick={() => setMobileDrawerOpen(true)}
                >
                  Continue
                </Fab>
              </Box>

              <Box sx={{ pt: 4 }}>
                <ContentWrapNarrow>
                  {/* In-app browser warning for Square checkout */}
                  {showInAppBrowserWarning &&
                    sellerProfile.payments.some(
                      (p) => p.type === PAYMENT_TYPE_CREDIT_CARD
                    ) && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>
                          For credit card checkout, use your phone’s browser
                          instead.
                        </AlertTitle>
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          Credit card payments may not work in in-app browsers.
                          For best results, tap "..." → "Open in external
                          browser".
                        </Typography>
                      </Alert>
                    )}

                  <CardStyle>
                    <Stack>
                      <Typography
                        variant="h5"
                        sx={{ mb: 1, textAlign: "center" }}
                      >
                        {getPresaleName(presaleInfo.presaleName)}
                      </Typography>
                      {presaleInfo.presaleDescription ? (
                        <Typography
                          variant="body1"
                          color="text.primary"
                          textAlign="center"
                          sx={{ mb: 2, whiteSpace: "pre-line" }}
                        >
                          {presaleInfo.presaleDescription}
                        </Typography>
                      ) : null}
                      <InfoBox>
                        <Typography variant="subtitle2" textAlign="center">
                          Pickup on{" "}
                          {formatFulfillments(presaleInfo.fulfillments)}
                        </Typography>
                      </InfoBox>
                    </Stack>
                  </CardStyle>
                </ContentWrapNarrow>
              </Box>

              <PresaleItems presaleItems={presaleInfo.presaleItems} />
            </Stack>
          </Box>
          {/* Persistent Drawer */}
          <Drawer
            variant="permanent"
            anchor="right"
            sx={{
              display: { xs: "none", md: "block" },
              width: drawerWidth,
              flexShrink: 0,
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                top: 49,
                height: "calc(100% - 49px)",
              },
            }}
          >
            <Box sx={{ overflowY: "auto", height: "100%" }}>
              <PresaleForm
                presaleInfo={presaleInfo}
                editing={false}
                sellerProfile={sellerProfile}
              />
            </Box>
          </Drawer>
          {/* Mobile: Temporary Drawer */}
          <Drawer
            variant="temporary"
            anchor="bottom"
            open={mobileDrawerOpen}
            onClose={() => setMobileDrawerOpen(false)}
            keepMounted={true}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                maxHeight: "90vh",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            }}
          >
            <Box sx={{ pb: 2 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ p: 2, pb: 0 }}
              >
                <Typography variant="h6">Your Order</Typography>
                <IconButton onClick={() => setMobileDrawerOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <PresaleForm
                presaleInfo={presaleInfo}
                editing={false}
                sellerProfile={sellerProfile}
              />
            </Box>
          </Drawer>
        </>
      )}
    </ThemeProvider>
  );
};

export default CustomerPresale;
